import { forwardRef } from "react";
import { createLink } from "@tanstack/react-router";
import { type VariantProps } from "class-variance-authority";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/cn";

/**
 * The three things this console asks of a button that the fleet's own one does
 * not do, kept out of `ui/button.tsx` so that file stays a byte-for-byte copy.
 *
 * The rule is worth stating once: anything under `ui/` is the borrowed layer and
 * is diffed against `packages/ui` in the fleet's repository. A prop added there
 * is a prop that has to be re-added by hand at every update, and the first one
 * that is forgotten is a silent behaviour change. So the additions live here,
 * one import away, and the copy stays empty of us.
 */

/**
 * A button that is busy: disabled **and** relabelled.
 *
 * Disabling is the half that matters — it is what stops a second submit,
 * whether the first was reached by a click, by Enter or by an assistive device
 * (this was a hand-written script on the server-rendered console). The label is
 * the half a person sees, and `aria-busy` the half a screen reader hears, since
 * a button that merely stops responding is indistinguishable from one that did
 * not register the click.
 *
 * The fleet has `FormSubmitButton` for the same idea, but it reads its state
 * from its own form context; these call sites hold a mutation's `isPending` and
 * pass it in.
 */
export const BusyButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { busy?: boolean; busyLabel?: string }
>(function BusyButton({ busy = false, busyLabel, children, disabled, ...props }, ref) {
  return (
    <Button ref={ref} disabled={disabled || busy} aria-busy={busy || undefined} {...props}>
      {busy && busyLabel ? busyLabel : children}
    </Button>
  );
});

/**
 * The same shape as a link, which is what "← Back" and "Go to sign in" have
 * always been.
 *
 * Kept as an anchor rather than a button that calls `location.assign`: a link is
 * what a person can middle-click, copy or open in a new tab, and turning one
 * into a button takes all three away for nothing.
 */
export function ButtonLink({
  className,
  variant,
  size,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & VariantProps<typeof buttonVariants>) {
  return <a data-slot="button" className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

/**
 * And the same shape again, as a route the app navigates to itself.
 *
 * `ButtonLink` reloads the document, which is right where it is used: the
 * sign-in screen's "← Back" wants the field emptied and the screen remounted.
 * Moving between the console's own screens is the opposite case — the session,
 * the workspace list and the sites are already in the cache, and a reload throws
 * all three away to fetch them again.
 */
const ButtonAnchor = forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement> & VariantProps<typeof buttonVariants>
>(function ButtonAnchor({ className, variant, size, ...props }, ref) {
  return (
    <a
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});

/*
 * `createLink` and not an `<a>` inside a `<Link>`: wrapping the router's own
 * component throws away the types that make `to` and `params` checked against
 * the route tree, and a mistyped `params` key is exactly the kind of thing that
 * then fails at runtime as a route that does not match.
 */
export const ButtonNav = createLink(ButtonAnchor);
